import type { Sheet, WireEdge, CompNode, BlockNode } from './store';
import type { Pin, PinSide, PinKind, ProjectMeta } from './types';

/**
 * A small, self-contained demo project loaded from the welcome screen so a
 * first-time user lands on something real instead of a blank canvas. It shows
 * the whole flow end to end on a deliberately tiny — but TWO-subsystem —
 * machine, so the between-subsystems kit is on display:
 *   • System Overview  — one block per subsystem with the Motor Cable between
 *   • Control Box       — Requirements / Block / Schematic (PSU + MCU + Driver +
 *                         X1 connector, internal harnesses) / Mechanical
 *   • Conveyor          — Block / Schematic (X2 connector + gear motor) / Mechanical
 *   • Harnesses         — the in-box harnesses (power / control / motor-out /
 *                         motor-in) + the Motor Cable between the enclosures
 *   • System Wiring     — the between-subsystems, pin-to-pin link (boundary
 *                         connectors X1 ↔ X2, TTAF TT20), where the Motor Cable
 *                         pinout is auto-derived from
 * Everything is hand-authored with stable ids so the wires line up exactly.
 */

const RED = '#dc2626'; // +24 V
const BLK = '#334155'; // 0 V / GND
const SIG = '#2563eb'; // control signals
const MOT = '#7c3aed'; // motor phases

// Harness ids — shared by the schematic wires (which drive the auto-derived
// connector tables) and the block / overview edges (which show the name).
const HARN_PWR_MCU = 'demo-harness-pwr-mcu'; // within Control Box
const HARN_PWR_DRV = 'demo-harness-pwr-drv'; // within Control Box
const HARN_CTRL = 'demo-harness-ctrl'; // within Control Box
const HARN_MOTOR = 'demo-harness-motor'; // BETWEEN Control Box → Conveyor
const HARN_CB_MOTOR = 'demo-harness-cb-motor'; // within Control Box: driver → X1
const HARN_CV_MOTOR = 'demo-harness-cv-motor'; // within Conveyor: X2 → motor

function pin(id: string, name: string, side: PinSide, kind: PinKind, group?: string): Pin {
  return { id, name, side, kind, ...(group ? { group } : {}) };
}

function comp(
  id: string,
  x: number,
  y: number,
  label: string,
  category: string,
  color: string,
  pins: Pin[],
  extra?: { partNumber?: string; manufacturer?: string; properties?: Record<string, string> },
): CompNode {
  return {
    id,
    type: 'component',
    position: { x, y },
    data: {
      label,
      category,
      color,
      partNumber: extra?.partNumber,
      manufacturer: extra?.manufacturer,
      properties: extra?.properties ?? {},
      pins,
    },
  };
}

function wire(
  id: string,
  source: string,
  sourceHandle: string,
  target: string,
  targetHandle: string,
  net: string,
  color: string,
  harnessId: string | undefined,
  opts?: { gauge?: string; wireNo?: string },
): WireEdge {
  return {
    id,
    source,
    target,
    sourceHandle,
    targetHandle,
    type: 'adjustable',
    label: net,
    data: {
      label: net,
      netName: net,
      color,
      gauge: opts?.gauge ?? '',
      wireNo: opts?.wireNo ?? '',
      notes: '',
      ...(harnessId ? { harnessId } : {}),
    },
  };
}

function block(
  id: string,
  x: number,
  y: number,
  title: string,
  subtitle: string,
  color: string,
  size?: { width?: number; height?: number },
): BlockNode {
  return {
    id,
    type: 'block',
    position: { x, y },
    data: {
      title,
      subtitle,
      color,
      textColor: '#ffffff',
      shape: 'rounded',
      width: size?.width ?? 200,
      height: size?.height ?? 96,
    },
  };
}

function blockEdge(
  id: string,
  source: string,
  target: string,
  label: string,
  harnessId: string,
  color: string,
): WireEdge {
  return {
    id,
    source,
    target,
    sourceHandle: 'r',
    targetHandle: 'l',
    type: 'adjustable',
    label,
    data: { label, gauge: '', color, notes: '', harnessId },
  };
}

/** Build a fresh copy of the demo project (safe to call repeatedly). */
export function buildSampleProject(): {
  meta: ProjectMeta;
  sheets: Sheet[];
  activeSheetId: string;
} {
  const now = new Date().toISOString();

  /* ================= Control Box — schematic ============================= */
  const psu = comp('demo-psu', 60, 120, '24 VDC Power Supply', 'Power', RED, [
    pin('psu-l', 'L', 'left', 'power', 'AC IN'),
    pin('psu-n', 'N', 'left', 'power', 'AC IN'),
    pin('psu-pe', 'PE', 'left', 'gnd', 'AC IN'),
    pin('psu-24', '+24V', 'right', 'power', 'DC OUT'),
    pin('psu-0', '0V', 'right', 'gnd', 'DC OUT'),
  ], { partNumber: 'RS-100-24', manufacturer: 'MeanWell', properties: { Output: '24 V / 4.2 A' } });

  const mcu = comp('demo-mcu', 60, 420, 'MCU', 'Controller', SIG, [
    pin('mcu-vin', 'VIN', 'left', 'power', 'PWR'),
    pin('mcu-gnd', 'GND', 'left', 'gnd', 'PWR'),
    pin('mcu-io1', 'IO2', 'right', 'pwm', 'IO'),
    pin('mcu-io2', 'IO4', 'right', 'signal', 'IO'),
    pin('mcu-io3', 'IO5', 'right', 'signal', 'IO'),
  ], { partNumber: 'ESP32-DEVKIT', manufacturer: 'Espressif', properties: { Logic: '3.3 V' } });

  const drv = comp('demo-drv', 480, 240, 'DC Motor Driver', 'Driver', '#0f766e', [
    pin('drv-24', '+24V', 'left', 'power', 'PWR'),
    pin('drv-0', 'GND', 'left', 'gnd', 'PWR'),
    pin('drv-pwm', 'PWM', 'left', 'pwm', 'CTRL'),
    pin('drv-dir', 'DIR', 'left', 'signal', 'CTRL'),
    pin('drv-en', 'EN', 'left', 'signal', 'CTRL'),
    pin('drv-mp', 'M+', 'right', 'power', 'MOTOR'),
    pin('drv-mn', 'M-', 'right', 'power', 'MOTOR'),
  ], { partNumber: 'BTS7960', manufacturer: 'Infineon', properties: { Current: '43 A peak' } });

  // Boundary connector — the Control Box's motor-out connector (X1). The cable
  // to the conveyor mates here; the run itself is drawn on System Wiring.
  const cbX1 = comp('demo-cb-x1', 900, 300, 'TTAF TT20 (2-pin)', 'Connector', BLK, [
    pin('cbx1-1', '1', 'left', 'power', 'X1'),
    pin('cbx1-2', '2', 'left', 'power', 'X1'),
  ], { manufacturer: 'TTAF', partNumber: 'TT20', properties: { Type: '2-pole power connector · panel plug' } });

  const cbWires: WireEdge[] = [
    wire('w-24-mcu', 'demo-psu', 'psu-24', 'demo-mcu', 'mcu-vin', '+24V', RED, HARN_PWR_MCU, { gauge: '18 AWG', wireNo: 'W1' }),
    wire('w-0-mcu', 'demo-psu', 'psu-0', 'demo-mcu', 'mcu-gnd', 'GND', BLK, HARN_PWR_MCU, { gauge: '18 AWG', wireNo: 'W2' }),
    wire('w-24-drv', 'demo-psu', 'psu-24', 'demo-drv', 'drv-24', '+24V', RED, HARN_PWR_DRV, { gauge: '16 AWG', wireNo: 'W3' }),
    wire('w-0-drv', 'demo-psu', 'psu-0', 'demo-drv', 'drv-0', 'GND', BLK, HARN_PWR_DRV, { gauge: '16 AWG', wireNo: 'W4' }),
    wire('w-pwm', 'demo-mcu', 'mcu-io1', 'demo-drv', 'drv-pwm', 'PWM', SIG, HARN_CTRL, { gauge: '24 AWG', wireNo: 'W5' }),
    wire('w-dir', 'demo-mcu', 'mcu-io2', 'demo-drv', 'drv-dir', 'DIR', SIG, HARN_CTRL, { gauge: '24 AWG', wireNo: 'W6' }),
    wire('w-en', 'demo-mcu', 'mcu-io3', 'demo-drv', 'drv-en', 'EN', SIG, HARN_CTRL, { gauge: '24 AWG', wireNo: 'W7' }),
    // In-box motor harness: driver output to the X1 panel connector.
    wire('w-mp-x1', 'demo-drv', 'drv-mp', 'demo-cb-x1', 'cbx1-1', 'MOTOR+', MOT, HARN_CB_MOTOR, { gauge: '16 AWG' }),
    wire('w-mn-x1', 'demo-drv', 'drv-mn', 'demo-cb-x1', 'cbx1-2', 'MOTOR-', MOT, HARN_CB_MOTOR, { gauge: '16 AWG' }),
  ];

  /* ================= Conveyor — schematic ================================ */
  const mot = comp('demo-mot', 520, 240, 'DC Gear Motor', 'Actuator', MOT, [
    pin('mot-mp', 'M+', 'left', 'power', 'J1'),
    pin('mot-mn', 'M-', 'left', 'power', 'J1'),
  ], { partNumber: 'GM37-24', properties: { Voltage: '24 V DC', Gearing: '30:1' } });

  const cvX2 = comp('demo-cv-x2', 120, 240, 'TTAF TT20 (2-pin)', 'Connector', BLK, [
    pin('cvx2-1', '1', 'right', 'power', 'X2'),
    pin('cvx2-2', '2', 'right', 'power', 'X2'),
  ], { manufacturer: 'TTAF', partNumber: 'TT20', properties: { Type: '2-pole power connector · cable socket' } });

  const cvWires: WireEdge[] = [
    // In-box motor harness: the X2 panel connector to the gear motor.
    wire('w-x2-mp', 'demo-cv-x2', 'cvx2-1', 'demo-mot', 'mot-mp', 'MOTOR+', MOT, HARN_CV_MOTOR, { gauge: '16 AWG' }),
    wire('w-x2-mn', 'demo-cv-x2', 'cvx2-2', 'demo-mot', 'mot-mn', 'MOTOR-', MOT, HARN_CV_MOTOR, { gauge: '16 AWG' }),
  ];

  /* ================= System Wiring — between subsystems =================== */
  // The Motor Cable's pin table is auto-derived from these two boundary
  // connectors, mated one-to-one across the enclosure boundary.
  const swCb = comp('demo-sw-cb', 120, 200, 'TTAF TT20 · Control Box', 'Connector', BLK, [
    pin('swcb-1', 'M+', 'right', 'power', 'X1'),
    pin('swcb-2', 'M-', 'right', 'power', 'X1'),
  ], { manufacturer: 'TTAF', partNumber: 'TT20' });
  const swCv = comp('demo-sw-cv', 640, 200, 'TTAF TT20 · Conveyor', 'Connector', BLK, [
    pin('swcv-1', 'M+', 'left', 'power', 'X2'),
    pin('swcv-2', 'M-', 'left', 'power', 'X2'),
  ], { manufacturer: 'TTAF', partNumber: 'TT20' });
  const swWires: WireEdge[] = [
    wire('w-cable-p', 'demo-sw-cb', 'swcb-1', 'demo-sw-cv', 'swcv-1', 'MOTOR+', MOT, HARN_MOTOR, { gauge: '16 AWG', wireNo: 'W8' }),
    wire('w-cable-n', 'demo-sw-cb', 'swcb-2', 'demo-sw-cv', 'swcv-2', 'MOTOR-', MOT, HARN_MOTOR, { gauge: '16 AWG', wireNo: 'W9' }),
  ];

  /* ================= Block diagrams ====================================== */
  const cbBlocks: BlockNode[] = [
    block('b-psu', 80, 100, '24 VDC PSU', 'RS-100-24', RED),
    block('b-mcu', 80, 300, 'MCU', 'ESP32 controller', SIG),
    block('b-drv', 420, 200, 'Motor Driver', 'BTS7960', '#0f766e'),
    block('b-cb-x1', 760, 200, 'TTAF TT20', 'X1 · motor-out connector', BLK),
  ];
  const cbBlockEdges: WireEdge[] = [
    blockEdge('be-psu-mcu', 'b-psu', 'b-mcu', 'MCU Power', HARN_PWR_MCU, RED),
    blockEdge('be-psu-drv', 'b-psu', 'b-drv', 'Driver Power', HARN_PWR_DRV, RED),
    blockEdge('be-mcu-drv', 'b-mcu', 'b-drv', 'Control Harness', HARN_CTRL, SIG),
    blockEdge('be-drv-x1', 'b-drv', 'b-cb-x1', 'Motor Out', HARN_CB_MOTOR, MOT),
  ];
  const cvBlocks: BlockNode[] = [
    block('b-cv-x2', 80, 160, 'TTAF TT20', 'X2 · motor-in connector', BLK),
    block('b-mot', 420, 160, 'DC Gear Motor', 'GM37-24', MOT),
  ];
  const cvBlockEdges: WireEdge[] = [
    blockEdge('be-x2-mot', 'b-cv-x2', 'b-mot', 'Motor In', HARN_CV_MOTOR, MOT),
  ];

  /* ================= System Overview (the kit) =========================== */
  const ovCb = block('ov-cb', 120, 140, 'Control Box', 'PSU · MCU · Driver', '#1e40af', { width: 220, height: 110 });
  const ovCv = block('ov-cv', 620, 140, 'Conveyor', 'Gear motor', '#1e40af', { width: 220, height: 110 });
  const ovEdge = blockEdge('ov-e-motor', 'ov-cb', 'ov-cv', 'Motor Cable', HARN_MOTOR, MOT);

  const sheets: Sheet[] = [
    {
      id: 'demo-overview',
      name: 'System Overview',
      kind: 'block',
      nodes: [ovCb, ovCv],
      edges: [ovEdge],
    },
    {
      id: 'demo-cb-req',
      name: 'Control Box — Requirements',
      kind: 'requirements',
      nodes: [],
      edges: [],
      requirements: [
        {
          id: 'req-1',
          reqNumber: 'R1',
          category: 'Functional',
          priority: 'Must',
          status: 'Approved',
          title: 'Drive a 24 V DC gear motor with variable speed',
          description:
            'The drive shall run a 24 VDC brushed gear motor and vary its speed from 0–100% via a PWM input, in both directions.',
          verification: 'Bench test: sweep PWM 0–100% in each direction and confirm smooth speed control.',
        },
        {
          id: 'req-2',
          reqNumber: 'R2',
          category: 'Interface',
          priority: 'Must',
          status: 'Approved',
          title: 'Single 24 VDC supply',
          description: 'All Control Box electronics shall run from one 24 VDC / 4 A supply. No secondary supply is permitted.',
          verification: 'Inspection of the schematic power tree.',
        },
        {
          id: 'req-3',
          reqNumber: 'R3',
          category: 'Safety',
          priority: 'Should',
          status: 'Draft',
          title: 'Hardware enable line',
          description: 'The motor driver shall expose a hardware EN line that immediately stops the output when pulled low.',
          verification: 'Test: pull EN low while running and confirm the motor coasts to a stop.',
        },
      ],
    },
    {
      id: 'demo-cb-block',
      name: 'Control Box — Block',
      kind: 'block',
      nodes: cbBlocks,
      edges: cbBlockEdges,
    },
    {
      id: 'demo-cb-sch',
      name: 'Control Box — Schematic',
      kind: 'schematic',
      nodes: [psu, mcu, drv, cbX1],
      edges: cbWires,
    },
    {
      id: 'demo-cb-mech',
      name: 'Control Box — Mechanical',
      kind: 'mechanical',
      nodes: [],
      edges: [],
    },
    {
      id: 'demo-cv-block',
      name: 'Conveyor — Block',
      kind: 'block',
      nodes: cvBlocks,
      edges: cvBlockEdges,
    },
    {
      id: 'demo-cv-sch',
      name: 'Conveyor — Schematic',
      kind: 'schematic',
      nodes: [cvX2, mot],
      edges: cvWires,
    },
    {
      id: 'demo-cv-mech',
      name: 'Conveyor — Mechanical',
      kind: 'mechanical',
      nodes: [],
      edges: [],
    },
    {
      id: 'demo-harn',
      name: 'Harnesses',
      kind: 'interconnect',
      nodes: [],
      edges: [],
      bundles: [
        {
          id: HARN_PWR_MCU,
          name: 'MCU Power',
          fromUnit: 'Control Box',
          toUnit: 'Control Box',
          conduit: 'Spiral wrap 6 mm',
          entryFrom: '',
          entryTo: '',
          cables: '2 × 18 AWG (+24V / 0V)',
          length: '0.3 m',
          revision: 'A',
          notes: 'In-box power feed from the PSU to the MCU.',
        },
        {
          id: HARN_PWR_DRV,
          name: 'Driver Power',
          fromUnit: 'Control Box',
          toUnit: 'Control Box',
          conduit: 'Spiral wrap 8 mm',
          entryFrom: '',
          entryTo: '',
          cables: '2 × 16 AWG (+24V / 0V)',
          length: '0.3 m',
          revision: 'A',
          notes: 'In-box power feed from the PSU to the motor driver — heavier gauge.',
        },
        {
          id: HARN_CTRL,
          name: 'Control Harness',
          fromUnit: 'Control Box',
          toUnit: 'Control Box',
          conduit: 'Braided sleeve 5 mm',
          entryFrom: '',
          entryTo: '',
          cables: '3 × 24 AWG (PWM / DIR / EN)',
          length: '0.3 m',
          revision: 'A',
          notes: 'In-box signal harness carrying the three MCU IO lines to the driver.',
        },
        {
          id: HARN_CB_MOTOR,
          name: 'Motor Out (in-box)',
          fromUnit: 'Control Box',
          toUnit: 'Control Box',
          conduit: 'Braided sleeve 8 mm',
          entryFrom: 'Motor Driver',
          entryTo: 'TTAF TT20 (X1)',
          cables: '2 × 1.5 mm² (M+ / M-)',
          length: '0.25 m',
          revision: 'A',
          notes: 'In-box motor harness from the driver output to the X1 panel connector.',
        },
        {
          id: HARN_MOTOR,
          name: 'Motor Cable',
          fromUnit: 'Control Box',
          toUnit: 'Conveyor',
          conduit: 'Braided sleeve 8 mm',
          entryFrom: 'TTAF TT20 (X1)',
          entryTo: 'TTAF TT20 (X2)',
          cables: '2 × 1.5 mm² (M+ / M-)',
          length: '1.5 m',
          revision: 'A',
          notes: 'The between-subsystems cable. Its pin table is auto-derived from the X1 ↔ X2 wires on the System Wiring sheet.',
        },
        {
          id: HARN_CV_MOTOR,
          name: 'Motor In (in-box)',
          fromUnit: 'Conveyor',
          toUnit: 'Conveyor',
          conduit: 'Braided sleeve 8 mm',
          entryFrom: 'TTAF TT20 (X2)',
          entryTo: 'DC Gear Motor',
          cables: '2 × 1.5 mm² (M+ / M-)',
          length: '0.25 m',
          revision: 'A',
          notes: 'In-box motor harness from the X2 panel connector to the gear motor.',
        },
      ],
    },
    {
      id: 'demo-sys-wiring',
      name: 'System Wiring',
      kind: 'schematic',
      nodes: [swCb, swCv],
      edges: swWires,
    },
  ];

  return {
    meta: {
      name: 'Example — Conveyor Drive',
      author: 'Skematiq',
      description:
        'A tiny two-subsystem demo: a Control Box (PSU + MCU + motor driver) driving a Conveyor gear motor. Explore the System Overview, each subsystem’s Block / Schematic sheets, the Harnesses (three in-box + the Motor Cable between the enclosures) and the System Wiring that links them — then edit or delete anything.',
      createdAt: now,
      updatedAt: now,
      systemKitProvisioned: true,
    },
    sheets,
    activeSheetId: 'demo-overview',
  };
}
